import { useState } from "react";
import { Input } from "@/components/ui/Input/Input";
import { Button, Textarea } from "../../components/ui";

const TOP_BLOCKS = [
  {
    title: "For Founders",
    details: [
      "WhatsApp: [Number]",
      "Chat with Fundy directly for guidance and support.",
      "Email: support@fundy.co.ke",
      "For account issues, technical support, or feedback.",
    ],
  },
  {
    title: "For Partners",
    details: [
      "Interested in joining the African Deal Stack consortium or integrating with Fundy?",
      "Email: partnerships@fundy.co.ke",
    ],
  },
  {
    title: "For Investors",
    details: [
      "Want to receive qualified deal flow through Fundy?",
      "Email: investors@fundy.co.ke",
      "General Inquiries",
      "Email: hello@fundy.co.ke",
    ],
  },
];

const BOTTOM_BLOCKS = [
  {
    title: "Address",
    details: [
      "UTUmoja Technologies Limited",
      "Diamond Plaza II, Parklands Avenue",
      "P.O. Box 2295-00606",
      "Nairobi, Kenya",
    ],
  },
  {
    title: "Press & Media",
    details: ["Email: press@fundy.co.ke"],
  },
];

type TContactForm = {
  name: string;
  phone: string;
  email: string;
  message: string;
};

export const Contact = () => {
  const [form, setForm] = useState<TContactForm>({ name: "", phone: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <article>
      {/* Hero */}
      <section className="relative min-h-[40vh] overflow-hidden bg-black">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url('/contact-us.png')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
        <div className="relative z-10 flex min-h-[40vh] items-end px-6 pb-16 pt-32 sm:px-10 lg:px-20">
          <div className="space-y-3">
            <p className="animate-fade-up text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">
              Get in touch
            </p>
            <h1 className="animate-fade-up text-[26px] font-bold tracking-tight text-white [animation-delay:100ms] sm:text-[32px]">
              Contact <span className="text-primary">us</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="bg-white px-6 py-20 sm:px-10 lg:px-20">
        <div className="grid gap-12 lg:grid-cols-3">
            {/* Column 1: For Founders, For Partners, For Investors — stacked */}
            <div className="space-y-8">
              {TOP_BLOCKS.map((block) => (
                <div key={block.title} className="space-y-2">
                  <h3 className="text-[16px] font-semibold tracking-tight text-body-text">{block.title}</h3>
                  {block.details.map((line, i) => (
                    <p key={i} className="text-[15px] font-normal leading-relaxed text-body-text/75">
                      {line.startsWith("Email:") ? (
                        <>
                          <span>Email: </span>
                          <a
                            href={`mailto:${line.split("Email: ")[1]}`}
                            className="rounded-sm font-medium text-body-text underline decoration-primary/40 underline-offset-4 transition-colors duration-150 hover:text-primary hover:decoration-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {line.split("Email: ")[1]}
                          </a>
                        </>
                      ) : (
                        line
                      )}
                    </p>
                  ))}
                </div>
              ))}
            </div>

            {/* Column 2: Address + Press & Media — stacked */}
            <div className="space-y-8">
              {BOTTOM_BLOCKS.map((block) => (
                <div key={block.title} className="space-y-2">
                  <h3 className="text-[16px] font-semibold tracking-tight text-body-text">{block.title}</h3>
                  {block.details.map((line, i) => (
                    <p key={i} className="text-[15px] font-normal leading-relaxed text-body-text/75">
                      {line.startsWith("Email:") ? (
                        <>
                          <span>Email: </span>
                          <a
                            href={`mailto:${line.split("Email: ")[1]}`}
                            className="rounded-sm font-medium text-body-text underline decoration-primary/40 underline-offset-4 transition-colors duration-150 hover:text-primary hover:decoration-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {line.split("Email: ")[1]}
                          </a>
                        </>
                      ) : (
                        line
                      )}
                    </p>
                  ))}
                </div>
              ))}
            </div>

            {/* Contact form */}
            <div className="rounded-2xl border border-black/5 bg-light-gray p-8 shadow-sm">
              <h2 className="mb-6 text-[16px] font-semibold tracking-tight text-body-text">Leave a message</h2>
              {submitted ? (
                <div className="flex h-48 items-center justify-center">
                  <p className="text-base font-medium text-primary">
                    Thank you! We&apos;ll be in touch shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-body-text/60" htmlFor="name">
                      Name
                    </label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="Your name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      className="bg-white text-body-text placeholder:text-body-text/40"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-body-text/60" htmlFor="phone">
                      Phone
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+254 700 000 000"
                      value={form.phone}
                      onChange={handleChange}
                      className="bg-white text-body-text placeholder:text-body-text/40"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-body-text/60" htmlFor="email">
                      Email
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={handleChange}
                      required
                      className="bg-white text-body-text placeholder:text-body-text/40"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-medium text-body-text/60" htmlFor="message">
                      Message
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="How can we help?"
                      value={form.message}
                      onChange={handleChange}
                      minHeight={100}
                      maxHeight={300}
                      required
                      className="border-gray-200 bg-white text-body-text placeholder:text-body-text/40 dark:bg-white"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full rounded-full bg-primary text-sm font-semibold text-white hover:bg-primary/90"
                  >
                    Submit
                  </Button>
                </form>
              )}
            </div>
        </div>
      </section>
    </article>
  );
};
